/**
 * La campana del storefront (ADR 005 de este repo; ADR 224 de CardVault, F5a).
 *
 * El mismo centro de notificaciones que la app, en el header del tema: el
 * botón circular junto al carrito, el badge de dos tonos y la bandeja lateral
 * con "Te toca actuar" arriba y la actividad agrupada por asunto abajo. Es un
 * puerto de `components/notificaciones/` de CardVault (`Campana.tsx`,
 * `Bandeja.tsx`, `use-campana.ts`, `agrupar.ts`, `tiempos.ts`): la regla de
 * ADR 148 es que las dos plataformas se vean y se porten igual.
 *
 * ── Cómo llega la sesión ───────────────────────────────────────────────────
 * Se pide a `cards.the-vault.mx` con `credentials: 'include'`. `the-vault.mx`
 * y `cards.the-vault.mx` son el mismo sitio, así que la cookie de sesión de la
 * app —host-only, `SameSite=Lax`— viaja sola, igual que en `vault-account-nav.js`
 * y en las pujas. Este script NUNCA ve un token.
 *
 * La cookie `vault_user` es estado visual (ADR 038/216): solo decide si vale
 * la pena intentar. Sin ella no se pinta ni se pide nada. La autoridad es la
 * sesión: un 401 esconde la campana.
 *
 * ── Sin avisos no hay campana (F6 de CardVault) ────────────────────────────
 * Con cero avisos en total (leídos o no) y cero acciones pendientes, el botón
 * no se pinta; con historial se queda, solo sin globo. En cuanto el polling
 * trae algo, aparece sin recargar. Si la respuesta no trae `total` (una
 * CardVault sin F6), la campana se porta como antes: se pinta.
 *
 * ── Lo que viene de la red nunca se interpreta como HTML ───────────────────
 * Todo texto remoto entra con `textContent`/`createElement`. Los `href` pasan
 * por `hrefSeguro` (solo la app y la tienda) y las imágenes por `imagenSegura`
 * (solo https). Los trazos de los íconos son una tabla fija de este archivo.
 */
(function () {
  'use strict';

  var ORIGEN_APP = 'https://cards.the-vault.mx';
  var PREFIJOS_VALIDOS = ['https://cards.the-vault.mx/', 'https://the-vault.mx/'];

  var CLAVE_CACHE = 'vault_campana_conteo_v1';
  /** El conteo se reusa entre páginas por este tiempo (mismo ritmo que el polling). */
  var VIGENCIA_MS = 60000;
  /** Cada cuánto se vuelve a contar con la pestaña visible (`use-campana.ts`). */
  var INTERVALO_MS = 60000;
  /** Foco y `visibilitychange` suelen llegar juntos: una sola petición. */
  var RESPIRO_MS = 5000;

  /**
   * El personaje de la casa (F6): el kaomoji de los globos del tutorial guiado
   * de CardVault (`components/ui/personaje-vault.tsx`). Los puntos llevan tilde
   * combinante (U+0303): no reescribir a mano. La cara cerrada es la del
   * parpadeo, con la misma ceja y el mismo ancho en Assistant.
   */
  var PERSONAJE = '{•̃_•̃}';
  var PERSONAJE_PARPADEO = '{-̃_-̃}';

  /** La cascada de entrada: cada fila ~35 ms después de la anterior, con tope. */
  var PASO_DE_ENTRADA_MS = 35;
  /** Después de 8 filas entran juntas: abrir no se debe sentir lento. */
  var TOPE_DE_ENTRADA = 8;

  var MIN = 60000;
  var HORA = 60 * MIN;
  var DIA = 24 * HORA;

  // ════════════════════════════════════════════════════════════════════════
  // Funciones puras — se exportan en `window.vaultCampana` para las pruebas
  // (docs/pruebas/vault-campana.nucleo.mjs).
  // ════════════════════════════════════════════════════════════════════════

  /**
   * Lo que dice el badge. Puerto exacto de `estadoDelBadge` (Campana.tsx):
   * ámbar ('accion') cuando hay una acción con plazo —gana, dice "te toca"—;
   * naranja ('nuevo') cuando solo hay no leídas.
   */
  function estadoDelBadge(conteo) {
    var noLeidas = numero(conteo && conteo.no_leidas);
    var acciones = numero(conteo && conteo.acciones_pendientes);
    var tono = acciones > 0 ? 'accion' : 'nuevo';
    var cuenta = noLeidas > 0 ? noLeidas : acciones;
    var partes = [];
    if (acciones > 0) partes.push(acciones + ' ' + (acciones === 1 ? 'acción pendiente' : 'acciones pendientes'));
    if (noLeidas > 0) partes.push(noLeidas + ' sin leer');
    return {
      visible: cuenta > 0,
      tono: tono,
      numero: cuenta,
      texto: cuenta > 99 ? '99+' : String(cuenta),
      etiqueta: partes.length > 0 ? 'Notificaciones: ' + partes.join(', ') : 'Notificaciones',
    };
  }

  /**
   * ¿Se pinta la campana? Puerto de `campanaVisible` (Campana.tsx, F6).
   * - Sin conteo todavía: no.
   * - Conteo sin `total` (CardVault sin F6): sí, como antes.
   * - Con `total`: solo si hay algún aviso o alguna acción pendiente.
   */
  function campanaVisible(conteo) {
    if (!conteo) return false;
    if (typeof conteo.total !== 'number') return true;
    return conteo.total > 0 || numero(conteo.acciones_pendientes) > 0 || numero(conteo.no_leidas) > 0;
  }

  /** Cuántas cartas caben en el abanico de un pedido (F7). */
  var CARTAS_EN_EL_ABANICO = 3;

  /**
   * Qué pinta la fila de un aviso (F7, *todo concepto se representa*). Puerto
   * de `visualDeLaFila` (Bandeja.tsx): el concepto lo declara el catálogo de
   * CardVault y llega en el aviso; `visual` llega ya resuelto (el mapa por la
   * ruta de la app, nunca una URL de Google armada aquí). Sin dato, el ícono.
   * Un aviso sin `concepto` (CardVault sin F7) se pinta como antes: su carta.
   */
  function visualDeLaFila(aviso) {
    // Solo se pinta la carta que trae imagen: sin ninguna (la cadena de ADR 087
    // de CardVault llegó al final), la fila cae a su ícono, no a un marco vacío.
    var cartas = ((aviso && aviso.cartas) || []).filter(function (c) {
      return !!(c && (imagenSegura(c.imagen_url) || imagenSegura(c.imagen_respaldo)));
    });
    var visual = (aviso && aviso.visual) || null;
    var concepto = aviso && aviso.concepto;
    if (!concepto) return cartas.length > 0 ? { tipo: 'carta', cartas: cartas } : { tipo: 'icono' };
    if (concepto === 'orden') {
      if (cartas.length >= 2) return { tipo: 'abanico', cartas: cartas.slice(0, CARTAS_EN_EL_ABANICO) };
      return cartas.length === 1 ? { tipo: 'carta', cartas: cartas } : { tipo: 'icono' };
    }
    if (concepto === 'carta') return cartas.length > 0 ? { tipo: 'carta', cartas: cartas } : { tipo: 'icono' };
    if (concepto === 'lugar') {
      var mapa = visual && visual.tipo === 'lugar' && visual.mapa;
      return mapa && imagenSegura(mapa.claro) && imagenSegura(mapa.oscuro)
        ? { tipo: 'mapa', mapa: { claro: mapa.claro, oscuro: mapa.oscuro } }
        : { tipo: 'pin' };
    }
    if (concepto === 'usuario') {
      if (!visual || visual.tipo !== 'usuario') return { tipo: 'icono' };
      var quien = String(visual.handle || visual.nombre || '?').replace(/^@/, '');
      return { tipo: 'avatar', foto: imagenSegura(visual.avatar_url), inicial: quien.charAt(0).toUpperCase() || '?' };
    }
    return { tipo: 'icono' };
  }

  /**
   * El tema del mapa según el fondo de la bandeja (`--color-background` de
   * Dawn, "r, g, b"): el esquema del header puede ser claro u oscuro.
   */
  function temaDelFondo(rgb) {
    var partes = String(rgb || '').split(',');
    if (partes.length < 3) return 'oscuro';
    var r = Number(partes[0]);
    var g = Number(partes[1]);
    var b = Number(partes[2]);
    if (!isFinite(r) || !isFinite(g) || !isFinite(b)) return 'oscuro';
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255 < 0.5 ? 'oscuro' : 'claro';
  }

  /**
   * ¿La bandeja está "al día"? Nada sin leer y nada por hacer: el personaje es
   * el protagonista. Puerto de `alDia` (Bandeja.tsx, F6).
   */
  function estaAlDia(pagina) {
    if (!pagina) return false;
    var acciones = pagina.acciones || [];
    var avisos = pagina.avisos || [];
    if (acciones.length > 0) return false;
    for (var i = 0; i < avisos.length; i++) {
      if (!avisos[i].read_at) return false;
    }
    return true;
  }

  /**
   * La entrada en cascada (F6). Recibe las claves de lo que se va a pintar, en
   * orden, y lo que ya entró en esta apertura. Devuelve el retraso de cada una
   * —`null` si ya entró: no se re-anima al repintar— y lo visto actualizado.
   * Las nuevas se escalonan entre sí; una sola que llega sola entra sin retraso.
   */
  function retrasosDeEntrada(claves, vistos) {
    var nuevos = {};
    var k;
    for (k in vistos) if (Object.prototype.hasOwnProperty.call(vistos, k)) nuevos[k] = true;
    var orden = 0;
    var retrasos = [];
    for (var i = 0; i < claves.length; i++) {
      if (nuevos[claves[i]]) {
        retrasos.push(null);
      } else {
        retrasos.push(Math.min(orden, TOPE_DE_ENTRADA) * PASO_DE_ENTRADA_MS);
        orden++;
        nuevos[claves[i]] = true;
      }
    }
    return { retrasos: retrasos, vistos: nuevos };
  }

  /**
   * ¿El conteo nuevo trae algo que la bandeja abierta no tiene? Con `total`
   * (F6) se compara el total; sin él, las no leídas. Puerto de Campana.tsx.
   */
  function llegoAlgoNuevo(antes, ahora) {
    if (!antes || !ahora) return false;
    if (typeof antes.total === 'number' && typeof ahora.total === 'number') return ahora.total > antes.total;
    return numero(ahora.no_leidas) > numero(antes.no_leidas);
  }

  /** El conteo tal como lo guarda la campana; `total` solo si vino como número. */
  function normalizarConteo(dato) {
    var c = { no_leidas: numero(dato.no_leidas), acciones_pendientes: numero(dato.acciones_pendientes) };
    if (typeof dato.total === 'number') c.total = numero(dato.total);
    return c;
  }

  function numero(valor) {
    var n = Number(valor);
    return isFinite(n) && n > 0 ? Math.floor(n) : 0;
  }

  /**
   * El destino de un aviso, o `null` si no es de la casa.
   *
   * - Relativo (`/dashboard/...`, sin `//`): es de la app, se le pone su origen.
   * - Absoluto: solo `https://cards.the-vault.mx/` o `https://the-vault.mx/`.
   * - Todo lo demás (`javascript:`, `//otro.com`, `http:`, otro dominio,
   *   espacios o caracteres de control) se descarta.
   */
  function hrefSeguro(href) {
    if (typeof href !== 'string' || href.length === 0 || href.length > 2000) return null;
    // Espacios y controles: un `java\tscript:` o un salto de línea no tienen
    // por qué estar en un enlace nuestro.
    if (/[\s\u0000-\u001f\u007f\\]/.test(href)) return null;
    if (href.charAt(0) === '/') {
      if (href.charAt(1) === '/') return null;
      return ORIGEN_APP + href;
    }
    for (var i = 0; i < PREFIJOS_VALIDOS.length; i++) {
      if (href.indexOf(PREFIJOS_VALIDOS[i]) === 0) return href;
    }
    return null;
  }

  /** La miniatura solo si es https (ADR 125: la carta se ve; si no, el ícono). */
  function imagenSegura(url) {
    if (typeof url !== 'string' || url.indexOf('https://') !== 0) return null;
    if (/[\s"'<>\\]/.test(url)) return null;
    return url;
  }

  /**
   * La actividad agrupada por asunto. Puerto de `agrupar.ts`: lo que tiene
   * historia propia (pedido, subasta, oferta, punto) es su bloque; lo de la
   * cuenta (retiro, depósito, wishlist) va a "Tu cuenta". Los avisos llegan del
   * más nuevo al más viejo, así que el primer bloque es el de lo más reciente.
   */
  var CON_HISTORIA = { orden: true, subasta: true, oferta: true, punto: true };

  /**
   * El encabezado de un pedido dice qué es para ti y de qué carta, no su
   * número (feedback del owner, 2026-10-03): "Pedido #VA-2076" no distingue si
   * lo vendiste o lo compraste. Puerto de `etiquetaDePedido` (agrupar.ts).
   */
  function etiquetaDePedido(a) {
    var evento = String((a && a.event_type) || '');
    var rol = evento.indexOf('seller.') === 0 ? 'Venta' : evento.indexOf('buyer.') === 0 ? 'Compra' : null;
    var primera = a && a.cartas && a.cartas[0];
    var carta = primera && typeof primera.nombre === 'string' ? primera.nombre.trim() : '';
    if (rol && carta) return rol + ' · ' + carta;
    if (rol) return rol === 'Venta' ? 'Tu venta' : 'Tu compra';
    return carta ? 'Pedido · ' + carta : 'Pedido';
  }

  function agruparActividad(avisos) {
    var grupos = [];
    var indice = {};
    (avisos || []).forEach(function (a) {
      var propio = !!CON_HISTORIA[a.asunto_tipo];
      var clave = propio ? a.asunto_tipo + ':' + a.asunto_id : 'cuenta';
      var grupo = indice[clave];
      if (!grupo) {
        grupo = {
          clave: clave,
          etiqueta: !propio
            ? 'Tu cuenta'
            : a.asunto_tipo === 'orden'
              ? etiquetaDePedido(a)
              : a.asunto_etiqueta || 'Pedido',
          avisos: [],
          noLeidos: 0,
        };
        indice[clave] = grupo;
        grupos.push(grupo);
      }
      grupo.avisos.push(a);
      if (!a.read_at) grupo.noLeidos++;
    });
    return grupos;
  }

  // ── El tiempo, como lo dice la app (tiempos.ts) ──

  /** Cuánto falta, sin sujeto: "30 min", "68 h" hasta 3 días, "4 días". `null` si venció. */
  function cuantoFalta(at, ahora) {
    var ms = new Date(at).getTime() - (ahora || new Date()).getTime();
    if (!isFinite(ms) || ms <= 0) return null;
    if (ms < HORA) return Math.max(1, Math.round(ms / MIN)) + ' min';
    if (ms <= 3 * DIA) return Math.round(ms / HORA) + ' h';
    var dias = Math.floor(ms / DIA);
    return dias === 1 ? '1 día' : dias + ' días';
  }

  /**
   * Cuánto tiempo pasó desde `at`: "5 h" el primer día, luego "1 día", "4 días".
   * Sirve igual para lo que lleva vencido que para lo que lleva esperando.
   */
  function cuantoLleva(at, ahora) {
    var ms = (ahora || new Date()).getTime() - new Date(at).getTime();
    if (!isFinite(ms)) return '';
    if (ms < DIA) return Math.max(1, Math.floor(ms / HORA)) + ' h';
    var dias = Math.floor(ms / DIA);
    return dias === 1 ? '1 día' : dias + ' días';
  }

  /** Cuánto lleva vencido: "5 h" el primer día, luego "1 día", "4 días". */
  var cuantoAtraso = cuantoLleva;

  /**
   * Qué vence en cada acción y qué se dice cuando ya venció. El ÚNICO lugar del
   * tema que lo sabe por tipo (puerto de `PLAZO_POR_TIPO` de tiempos.ts; ADR 148:
   * las dos plataformas dicen lo mismo). Solo lo que vence DE VERDAD, con
   * consecuencia: el pago de una subasta y la decisión de un conflicto.
   * `entregar_venta` y `recoger_pedido` no vencen: ver `ESPERA_POR_TIPO`.
   */
  var PLAZO_POR_TIPO = {
    pagar_subasta: {
      vence: 'Pago',
      vencido: function () {
        return 'Pago vencido';
      },
    },
    decidir_conflicto: {
      vence: 'Decisión',
      vencido: function () {
        return 'Decisión vencida';
      },
    },
  };

  /**
   * Lo que espera sin vencer, dicho por tipo: cuánto lleva, nunca "vence" ni
   * "atrasada"; tono normal (ámbar) siempre (puerto de `ESPERA_POR_TIPO`).
   *
   * `entregar_venta` (hotfix 2026-09-28): no hay SLA del vendedor —es una
   * invitación, no un plazo—. Un vendedor que solo entrega los sábados leyó
   * "Entrega atrasada" como amenaza y escribió preocupado. Sin entrada
   * (`recoger_pedido`), el genérico "Lleva N días".
   */
  var ESPERA_POR_TIPO = {
    entregar_venta: function (lleva) {
      return 'Esperando entrega · ' + lleva;
    },
  };

  /**
   * El texto y el tono del chip de plazo de una acción (puerto de `chipDePlazo`
   * de tiempos.ts). Tono: 'normal', 'urgente' (<24 h) o 'vencido'. Un tipo que
   * el tema no conoce dice "Vence en…" / "Venció", nunca nada inventado.
   */
  function plazoDeLaAccion(accion, ahora) {
    var plazo = (accion && accion.plazo) || {};
    var tipo = accion && accion.tipo;
    var espera = Object.prototype.hasOwnProperty.call(ESPERA_POR_TIPO, tipo) ? ESPERA_POR_TIPO[tipo] : null;
    // Lo que no vence nunca se dice con "vence", aunque llegue con ese tipo.
    if (plazo.tipo !== 'vence' || espera || tipo === 'recoger_pedido') {
      return {
        texto: espera ? espera(cuantoLleva(plazo.at, ahora)) : llevaEsperando(plazo.at, ahora),
        tono: 'normal',
      };
    }
    var nombre = Object.prototype.hasOwnProperty.call(PLAZO_POR_TIPO, tipo) ? PLAZO_POR_TIPO[tipo] : null;
    var falta = cuantoFalta(plazo.at, ahora);
    if (falta === null) {
      return { texto: nombre ? nombre.vencido(cuantoAtraso(plazo.at, ahora)) : 'Venció', tono: 'vencido' };
    }
    return {
      texto: (nombre ? nombre.vence + ' vence en ' : 'Vence en ') + falta,
      tono: esUrgente(plazo.at, ahora) ? 'urgente' : 'normal',
    };
  }

  /**
   * Los avisos que marca el clic de una acción: todos los que absorbió (la
   * entrega agrupada cubre varios pedidos, `avisos_por_leer`), o su voz si
   * CardVault aún no los manda.
   */
  function avisosDeLaAccion(accion) {
    if (accion && Array.isArray(accion.avisos_por_leer)) return accion.avisos_por_leer.slice();
    return accion && accion.aviso_id ? [accion.aviso_id] : [];
  }

  /** "Lleva 4 días" para lo que espera sin vencimiento prometido. */
  function llevaEsperando(at, ahora) {
    var dias = Math.floor(((ahora || new Date()).getTime() - new Date(at).getTime()) / DIA);
    if (!isFinite(dias)) return '';
    if (dias <= 0) return 'Desde hoy';
    return dias === 1 ? 'Lleva 1 día' : 'Lleva ' + dias + ' días';
  }

  /** Menos de 24 h: el chip se enciende. */
  function esUrgente(at, ahora) {
    var ms = new Date(at).getTime() - (ahora || new Date()).getTime();
    return ms > 0 && ms < DIA;
  }

  /** "hace 2 h", "ayer", "hace 3 días", o la fecha corta. */
  function haceCuanto(at, ahora) {
    var ms = (ahora || new Date()).getTime() - new Date(at).getTime();
    if (!isFinite(ms)) return '';
    if (ms < MIN) return 'ahora';
    if (ms < HORA) return 'hace ' + Math.floor(ms / MIN) + ' min';
    if (ms < DIA) return 'hace ' + Math.floor(ms / HORA) + ' h';
    var dias = Math.floor(ms / DIA);
    if (dias === 1) return 'ayer';
    if (dias < 7) return 'hace ' + dias + ' días';
    try {
      return new Intl.DateTimeFormat('es-MX', {
        day: 'numeric',
        month: 'short',
        timeZone: 'America/Mexico_City',
      }).format(new Date(at));
    } catch (e) {
      return '';
    }
  }

  /** El ícono cuando el aviso no nombra una carta (IconoDelAviso de Bandeja.tsx). */
  function iconoDelAviso(evento, asunto) {
    evento = String(evento || '');
    if (asunto === 'subasta') return 'Gavel';
    if (
      asunto === 'retiro' ||
      asunto === 'deposito' ||
      evento.indexOf('saldo') !== -1 ||
      evento.indexOf('payment') !== -1 ||
      evento === 'hub.corte'
    ) {
      return 'Wallet';
    }
    if (asunto === 'oferta') return 'Tag';
    if (evento.indexOf('wishlist') === 0) return 'Heart';
    if (asunto === 'punto' || evento.indexOf('hub.') === 0) return 'Store';
    if (evento.indexOf('pickup') !== -1 || evento.indexOf('shipped') !== -1 || evento.indexOf('transit') !== -1) {
      return 'MapPin';
    }
    return 'Package';
  }

  /** ¿La cookie visual dice que hay alguien? (ADR 038: estado visual, no autoridad.) */
  function hayUsuarioVisual(cookie) {
    return /(?:^|;\s*)vault_user=[^;]+/.test(String(cookie || ''));
  }

  /** Lee el conteo guardado entre páginas; `null` si no hay o ya caducó. */
  function leerCacheDeConteo(crudo, ahora) {
    if (!crudo) return null;
    try {
      var dato = JSON.parse(crudo);
      if (!dato || typeof dato.t !== 'number') return null;
      var edad = ahora - dato.t;
      if (edad < 0 || edad > VIGENCIA_MS) return null;
      if (dato.sinSesion === true) return { sinSesion: true };
      if (!dato.conteo) return null;
      return { conteo: normalizarConteo(dato.conteo) };
    } catch (e) {
      return null;
    }
  }

  var puras = {
    estadoDelBadge: estadoDelBadge,
    campanaVisible: campanaVisible,
    normalizarConteo: normalizarConteo,
    estaAlDia: estaAlDia,
    visualDeLaFila: visualDeLaFila,
    temaDelFondo: temaDelFondo,
    retrasosDeEntrada: retrasosDeEntrada,
    llegoAlgoNuevo: llegoAlgoNuevo,
    PASO_DE_ENTRADA_MS: PASO_DE_ENTRADA_MS,
    TOPE_DE_ENTRADA: TOPE_DE_ENTRADA,
    PERSONAJE: PERSONAJE,
    PERSONAJE_PARPADEO: PERSONAJE_PARPADEO,
    hrefSeguro: hrefSeguro,
    imagenSegura: imagenSegura,
    agruparActividad: agruparActividad,
    etiquetaDePedido: etiquetaDePedido,
    cuantoFalta: cuantoFalta,
    cuantoAtraso: cuantoAtraso,
    cuantoLleva: cuantoLleva,
    plazoDeLaAccion: plazoDeLaAccion,
    PLAZO_POR_TIPO: PLAZO_POR_TIPO,
    ESPERA_POR_TIPO: ESPERA_POR_TIPO,
    avisosDeLaAccion: avisosDeLaAccion,
    llevaEsperando: llevaEsperando,
    esUrgente: esUrgente,
    haceCuanto: haceCuanto,
    iconoDelAviso: iconoDelAviso,
    hayUsuarioVisual: hayUsuarioVisual,
    leerCacheDeConteo: leerCacheDeConteo,
    VIGENCIA_MS: VIGENCIA_MS,
  };
  if (typeof window !== 'undefined') window.vaultCampana = puras;

  if (typeof document === 'undefined') return;

  // ════════════════════════════════════════════════════════════════════════
  // La campana en la página
  // ════════════════════════════════════════════════════════════════════════

  // Trazos exactos de lucide-react 0.469 (los que usa la app). Tabla fija.
  var ICONOS = {
    Bell:
      '<path d="M10.268 21a2 2 0 0 0 3.464 0"/><path d="M3.262 15.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326"/>',
    Gavel:
      '<path d="m14.5 12.5-8 8a2.119 2.119 0 1 1-3-3l8-8"/><path d="m16 16 6-6"/><path d="m8 8 6-6"/><path d="m9 7 8 8"/><path d="m21 11-8-8"/>',
    Wallet:
      '<path d="M19 7V4a1 1 0 0 0-1-1H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v4h-3a2 2 0 0 0 0 4h3a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1"/><path d="M3 5v14a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1v-4"/>',
    Tag:
      '<path d="M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z"/><circle cx="7.5" cy="7.5" r=".5" fill="currentColor"/>',
    Heart:
      '<path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/>',
    Store:
      '<path d="m2 7 4.41-4.41A2 2 0 0 1 7.83 2h8.34a2 2 0 0 1 1.42.59L22 7"/><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><path d="M15 22v-4a2 2 0 0 0-2-2h-2a2 2 0 0 0-2 2v4"/><path d="M2 7h20"/><path d="M22 7v3a2 2 0 0 1-2 2a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 16 12a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 12 12a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 8 12a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 4 12a2 2 0 0 1-2-2V7"/>',
    MapPin:
      '<path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"/><circle cx="12" cy="10" r="3"/>',
    Package:
      '<path d="M11 21.73a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73z"/><path d="M12 22V12"/><path d="m3.3 7 7.703 4.734a2 2 0 0 0 1.994 0L20.7 7"/><path d="m7.5 4.27 9 5.15"/>',
    Hourglass:
      '<path d="M5 22h14"/><path d="M5 2h14"/><path d="M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22"/><path d="M7 2v4.172a2 2 0 0 0 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2"/>',
    CheckCheck: '<path d="M18 6 7 17l-5-5"/><path d="m22 10-7.5 7.5L13 16"/>',
    ChevronDown: '<path d="m6 9 6 6 6-6"/>',
  };

  function icono(nombre, clase) {
    var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('class', clase || 'vault-campana__icono');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '2');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round');
    svg.setAttribute('aria-hidden', 'true');
    // Trazos de la tabla fija de arriba, no de la respuesta remota.
    svg.innerHTML = ICONOS[nombre] || ICONOS.Package;
    return svg;
  }

  function el(etiqueta, clase, texto) {
    var nodo = document.createElement(etiqueta);
    if (clase) nodo.className = clase;
    if (texto != null) nodo.textContent = String(texto);
    return nodo;
  }

  // ── Estado ──

  var boton = null;
  var burbuja = null;
  var panel = null;
  var cuerpo = null;
  var accionCabecera = null;

  var conteo = null;
  var pagina = null;
  var cargando = false;
  var error = false;
  var cargandoMas = false;
  var todoAlDia = false;
  /** Los bloques de actividad nacen colapsados (owner, 2026-10-03): solo se abre lo que se toca. */
  var abiertos = {};
  /** Lo que ya entró en esta apertura (claves de filas y grupos). */
  var yaEntraron = {};
  var enVuelo = false;
  var ultimaPeticion = 0;
  var reloj = null;
  var apagada = false;

  // ── Caché entre páginas ──

  function leerCache() {
    try {
      return leerCacheDeConteo(window.sessionStorage.getItem(CLAVE_CACHE), Date.now());
    } catch (e) {
      return null;
    }
  }

  function guardarCache(dato) {
    try {
      dato.t = Date.now();
      window.sessionStorage.setItem(CLAVE_CACHE, JSON.stringify(dato));
    } catch (e) {
      /* modo privado o storage lleno: sin caché, se vuelve a pedir */
    }
  }

  // ── Red ──

  function pedir(ruta, opciones) {
    var o = opciones || {};
    o.credentials = 'include';
    o.cache = 'no-store';
    return fetch(ORIGEN_APP + ruta, o);
  }

  /** Sin sesión en la app: la campana se esconde y deja de preguntar. */
  function apagar() {
    apagada = true;
    guardarCache({ sinSesion: true });
    if (reloj) window.clearInterval(reloj);
    reloj = null;
    cerrar();
    if (boton) boton.style.display = 'none';
  }

  function refrescarConteo() {
    if (apagada || enVuelo) return;
    if (Date.now() - ultimaPeticion < RESPIRO_MS) return;
    enVuelo = true;
    ultimaPeticion = Date.now();
    pedir('/api/notificaciones/conteo')
      .then(function (r) {
        if (r.status === 401) {
          apagar();
          return null;
        }
        if (!r.ok) return null;
        return r.json();
      })
      .then(function (dato) {
        if (!dato) return;
        var anterior = conteo;
        conteo = normalizarConteo(dato);
        guardarCache({ conteo: conteo });
        // Con la bandeja abierta, lo que llegue entra solo (con su animación).
        if (estaAbierta() && !cargando && llegoAlgoNuevo(anterior, conteo)) cargarBandeja();
        pintarBadge();
      })
      .catch(function () {
        /* sin red: la campana se queda con el último conteo */
      })
      .then(function () {
        enVuelo = false;
      });
  }

  function marcar(cuerpoPeticion) {
    return pedir('/api/notificaciones/leidas', {
      method: 'POST',
      // `text/plain` a propósito: es una petición CORS "simple" y no dispara
      // preflight. Un `keepalive` con preflight no está garantizado en todos
      // los navegadores, y este POST sale justo antes de navegar. La ruta lee
      // el cuerpo con `request.json()`, que no mira el Content-Type.
      headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
      body: JSON.stringify(cuerpoPeticion),
      // Sobrevive a la navegación que viene justo después del clic.
      keepalive: true,
    }).catch(function () {
      return null;
    });
  }

  function cargarBandeja() {
    cargando = true;
    error = false;
    pintarBandeja();
    pedir('/api/notificaciones')
      .then(function (r) {
        if (r.status === 401) {
          apagar();
          return null;
        }
        if (!r.ok) throw new Error(String(r.status));
        return r.json();
      })
      .then(function (dato) {
        if (dato) pagina = normalizarPagina(dato);
      })
      .catch(function () {
        error = true;
      })
      .then(function () {
        cargando = false;
        pintarBandeja();
      });
  }

  function cargarMas() {
    if (!pagina || !pagina.siguiente || cargandoMas) return;
    cargandoMas = true;
    pintarBandeja();
    pedir('/api/notificaciones?antes=' + encodeURIComponent(pagina.siguiente))
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (dato) {
        if (!dato || !pagina) return;
        var mas = normalizarPagina(dato);
        pagina.avisos = pagina.avisos.concat(mas.avisos);
        pagina.siguiente = mas.siguiente;
      })
      .catch(function () {
        /* se queda el botón para reintentar */
      })
      .then(function () {
        cargandoMas = false;
        pintarBandeja();
      });
  }

  function normalizarPagina(dato) {
    return {
      acciones: Array.isArray(dato.acciones) ? dato.acciones : [],
      avisos: Array.isArray(dato.avisos) ? dato.avisos : [],
      siguiente: typeof dato.siguiente === 'string' ? dato.siguiente : null,
    };
  }

  // ── Badge ──

  function pintarBadge() {
    if (!boton || !burbuja) return;
    // Abierta nunca se esconde: se cierra primero y luego decide.
    if (!apagada && conteo && !estaAbierta()) {
      var antes = boton.style.display !== 'none';
      var ahora = campanaVisible(conteo);
      boton.style.display = ahora ? '' : 'none';
      // Aparece con el siguiente poll: entra suave, no de golpe.
      if (ahora && !antes) {
        boton.classList.remove('is-apareciendo');
        void boton.offsetWidth;
        boton.classList.add('is-apareciendo');
      }
    }
    var b = estadoDelBadge(conteo);
    boton.setAttribute('aria-label', b.etiqueta);
    if (!b.visible) {
      burbuja.hidden = true;
      return;
    }
    var anterior = burbuja.getAttribute('data-tono') + ':' + burbuja.textContent;
    burbuja.setAttribute('data-tono', b.tono);
    burbuja.textContent = b.texto;
    burbuja.hidden = false;
    // El badge "late" cuando cambia el número o el tono (como en la app).
    if (anterior !== b.tono + ':' + b.texto) {
      burbuja.classList.remove('is-latiendo');
      void burbuja.offsetWidth;
      burbuja.classList.add('is-latiendo');
    }
  }

  // ── Bandeja ──

  function hayNoLeidos() {
    if (!pagina) return false;
    var i;
    for (i = 0; i < pagina.acciones.length; i++) {
      if (!pagina.acciones[i].leida && pagina.acciones[i].aviso_id) return true;
    }
    for (i = 0; i < pagina.avisos.length; i++) {
      if (!pagina.avisos[i].read_at) return true;
    }
    return false;
  }

  function pintarCabecera() {
    if (!accionCabecera) return;
    accionCabecera.textContent = '';
    if (todoAlDia && !hayNoLeidos()) {
      var chip = el('span', 'vault-campana__chip vault-campana__chip--ok');
      chip.appendChild(icono('CheckCheck', 'vault-campana__icono vault-campana__icono--chico'));
      chip.appendChild(document.createTextNode('Todo al día'));
      accionCabecera.appendChild(chip);
      return;
    }
    var b = el('button', 'vault-campana__marcar-todo', 'Marcar todo leído');
    b.type = 'button';
    b.disabled = !hayNoLeidos();
    b.addEventListener('click', marcarTodo);
    accionCabecera.appendChild(b);
  }

  function miniatura(cartas, evento, asunto) {
    var carta = cartas && cartas[0];
    var respaldo = carta && imagenSegura(carta.imagen_respaldo);
    var src = (carta && imagenSegura(carta.imagen_url)) || respaldo;
    if (src === respaldo) respaldo = null;
    if (!src) {
      var circulo = el('span', 'vault-campana__icono-aviso');
      circulo.appendChild(icono(iconoDelAviso(evento, asunto)));
      return circulo;
    }
    var marco = el('span', 'vault-campana__carta' + (carta.foil ? ' is-foil' : ''));
    var img = document.createElement('img');
    img.src = src;
    img.alt = String(carta.nombre || '');
    img.loading = 'lazy';
    img.decoding = 'async';
    img.width = 32;
    img.height = 45;
    // Si la imagen no carga: Scryfall (ADR 087, mecanismo B); si tampoco, el
    // concepto se sigue representando con su ícono.
    img.addEventListener('error', function () {
      if (respaldo) {
        img.src = respaldo;
        respaldo = null;
        return;
      }
      var circulo = el('span', 'vault-campana__icono-aviso');
      circulo.appendChild(icono(iconoDelAviso(evento, asunto)));
      if (marco.parentNode) marco.parentNode.replaceChild(circulo, marco);
    });
    marco.appendChild(img);
    return marco;
  }

  /** Una imagen decorativa (el título ya nombra lo que enseña). */
  function imagen(src, ancho, alto) {
    var img = document.createElement('img');
    img.src = src;
    img.alt = '';
    img.loading = 'lazy';
    img.decoding = 'async';
    img.width = ancho;
    img.height = alto;
    return img;
  }

  function circuloDeIcono(nombre) {
    var circulo = el('span', 'vault-campana__icono-aviso');
    circulo.appendChild(icono(nombre));
    return circulo;
  }

  /** El pedido enseña sus cartas: hasta 3 en abanico; solo la de enfrente lleva nombre. */
  function abanico(cartas, evento, asunto) {
    var caja = el('span', 'vault-campana__abanico');
    caja.setAttribute('data-visual', 'abanico');
    var atras = cartas.slice(1).reverse();
    atras.forEach(function (c, i) {
      var src = imagenSegura(c.imagen_url) || imagenSegura(c.imagen_respaldo);
      var capa = el('span', 'vault-campana__abanico-carta ' + (atras.length === 1 ? 'is-izq' : i === 0 ? 'is-izq' : 'is-der'));
      capa.setAttribute('aria-hidden', 'true');
      if (src) capa.appendChild(imagen(src, 32, 45));
      caja.appendChild(capa);
    });
    var frente = miniatura([cartas[0]], evento, asunto);
    frente.className += ' vault-campana__abanico-frente';
    caja.appendChild(frente);
    return caja;
  }

  /** El lugar se ubica: el mapa del punto en el tema de la bandeja. */
  function mapaDelPunto(mapa) {
    var marco = el('span', 'vault-campana__mapa');
    marco.setAttribute('data-visual', 'mapa');
    marco.setAttribute('aria-hidden', 'true');
    var tema = temaDelFondo(window.getComputedStyle(panel || document.body).getPropertyValue('--color-background'));
    var img = imagen(tema === 'claro' ? mapa.claro : mapa.oscuro, 44, 44);
    // Si el mapa no carga, el lugar se sigue representando: el pin.
    img.addEventListener('error', function () {
      var pin = circuloDeIcono('MapPin');
      if (marco.parentNode) marco.parentNode.replaceChild(pin, marco);
    });
    marco.appendChild(img);
    return marco;
  }

  /** La persona tiene cara: su foto, o su inicial en círculo. */
  function cara(foto, inicial) {
    var circulo = el('span', 'vault-campana__cara');
    circulo.setAttribute('data-visual', 'avatar');
    circulo.setAttribute('aria-hidden', 'true');
    var letra = el('span', 'vault-campana__cara-inicial', inicial);
    circulo.appendChild(letra);
    if (foto) {
      var img = imagen(foto, 36, 36);
      img.addEventListener('error', function () {
        if (img.parentNode) img.parentNode.removeChild(img);
      });
      circulo.appendChild(img);
    }
    return circulo;
  }

  /**
   * Lo que enseña una fila —aviso o acción—, según su concepto (F7): la misma
   * regla para las dos. `evento` y `asunto` solo eligen el ícono cuando no hay
   * nada que enseñar.
   */
  function visualEnFila(datos, evento, asunto) {
    var v = visualDeLaFila(datos);
    if (v.tipo === 'carta') return miniatura(v.cartas, evento, asunto);
    if (v.tipo === 'abanico') return abanico(v.cartas, evento, asunto);
    if (v.tipo === 'mapa') return mapaDelPunto(v.mapa);
    if (v.tipo === 'pin') return circuloDeIcono('MapPin');
    if (v.tipo === 'avatar') return cara(v.foto, v.inicial);
    return circuloDeIcono(iconoDelAviso(evento, asunto));
  }

  function visualDelAviso(aviso) {
    return visualEnFila(aviso, aviso.event_type, aviso.asunto_tipo);
  }

  function cuando(at, ahora) {
    if (!at) return null;
    return el('span', 'vault-campana__cuando', haceCuanto(at, ahora));
  }

  /** Una fila: un verbo, abrir. Enlace si tiene destino de la casa; si no, botón que solo marca. */
  function fila(datos) {
    var destino = hrefSeguro(datos.href);
    var nodo = el(destino ? 'a' : 'button', 'vault-campana__fila' + (datos.leida ? ' is-leida' : ''));
    if (destino) nodo.href = destino;
    else nodo.type = 'button';

    var punto = el('span', 'vault-campana__punto');
    punto.setAttribute('aria-hidden', 'true');
    nodo.appendChild(punto);
    // Una ranura fija: carta, abanico, mapa o cara, el texto arranca en la misma línea.
    var ranura = el('span', 'vault-campana__ranura');
    ranura.appendChild(datos.visual || miniatura(datos.cartas, datos.evento, datos.asunto));
    nodo.appendChild(ranura);

    var texto = el('span', 'vault-campana__texto');
    texto.appendChild(el('span', 'vault-campana__titulo', datos.titulo || ''));
    if (datos.cuerpo) texto.appendChild(el('span', 'vault-campana__cuerpo', datos.cuerpo));
    if (datos.nota) texto.appendChild(el('span', 'vault-campana__nota', datos.nota));
    var meta = el('span', 'vault-campana__meta');
    (datos.meta || []).forEach(function (m) {
      if (m) meta.appendChild(m);
    });
    texto.appendChild(meta);
    nodo.appendChild(texto);

    nodo.addEventListener('click', function (evento) {
      abrirAviso(evento, destino, datos.avisoIds);
    });
    return nodo;
  }

  function chipDePlazo(accion, ahora) {
    // Qué vence y qué pasó salen de `plazoDeLaAccion` (un mapa por tipo).
    var p = plazoDeLaAccion(accion, ahora);
    var chip = el(
      'span',
      'vault-campana__chip vault-campana__chip--plazo' +
        (p.tono === 'urgente' ? ' is-urgente' : p.tono === 'vencido' ? ' is-vencido' : '')
    );
    chip.setAttribute('data-tono', p.tono);
    chip.appendChild(icono('Hourglass', 'vault-campana__icono vault-campana__icono--chico'));
    chip.appendChild(el('span', 'vault-campana__chip-texto', p.texto));
    return chip;
  }

  function encabezado(texto) {
    var h = el('div', 'vault-campana__seccion');
    h.appendChild(el('span', null, texto));
    var linea = el('span', 'vault-campana__linea');
    linea.setAttribute('aria-hidden', 'true');
    h.appendChild(linea);
    return h;
  }

  function vacio(nombreIcono, texto, claseIcono) {
    var caja = el('div', 'vault-campana__vacio');
    caja.appendChild(icono(nombreIcono, 'vault-campana__icono ' + (claseIcono || '')));
    caja.appendChild(document.createTextNode(texto));
    return caja;
  }

  /** Pone la animación de entrada si le toca (`retraso` null/undefined: ya entró). */
  function entrar(nodo, retraso) {
    if (retraso == null) return;
    nodo.classList.add('vault-campana__entra');
    if (retraso > 0) nodo.style.animationDelay = retraso + 'ms';
  }

  /**
   * El personaje animado: dos caras apiladas en la misma celda (abierta y
   * cerrada). La animación —flotar y parpadear cada 6 s— es CSS del snippet,
   * quieta con `prefers-reduced-motion`. Es compañía, no información.
   */
  function personaje(clase) {
    var raiz = el('span', 'vault-personaje ' + (clase || ''));
    raiz.setAttribute('aria-hidden', 'true');
    raiz.appendChild(el('span', 'vault-personaje__cara vault-personaje__cara--abierta', PERSONAJE));
    raiz.appendChild(el('span', 'vault-personaje__cara vault-personaje__cara--cerrada', PERSONAJE_PARPADEO));
    return raiz;
  }

  /** El cierre de la lista: despedida discreta, o protagonista si está al día. */
  function despedida(alDia, entrando) {
    var caja = el('div', 'vault-campana__despedida' + (alDia ? ' is-al-dia' : '') + (entrando ? ' is-entrando' : ''));
    caja.setAttribute('data-despedida', alDia ? 'al-dia' : 'fondo');
    caja.appendChild(personaje(alDia ? 'vault-personaje--protagonista' : ''));
    if (alDia) {
      caja.appendChild(el('p', 'vault-campana__despedida-titulo', 'Estás al día'));
      caja.appendChild(el('p', 'vault-campana__despedida-texto', 'Te avisamos aquí en cuanto pase algo.'));
    } else {
      caja.appendChild(el('p', 'vault-campana__despedida-texto', 'Eso es todo por ahora.'));
    }
    return caja;
  }

  function pintarBandeja() {
    if (!cuerpo) return;
    pintarCabecera();
    cuerpo.textContent = '';
    var ahora = new Date();

    if (cargando && !pagina) {
      var esqueleto = el('div', 'vault-campana__esqueleto');
      esqueleto.setAttribute('aria-label', 'Cargando notificaciones');
      for (var i = 0; i < 3; i++) {
        var renglon = el('div', 'vault-campana__esqueleto-fila');
        renglon.appendChild(el('span', 'vault-campana__esqueleto-carta'));
        var lineas = el('span', 'vault-campana__esqueleto-lineas');
        lineas.appendChild(el('span', 'vault-campana__esqueleto-linea is-corta'));
        lineas.appendChild(el('span', 'vault-campana__esqueleto-linea'));
        renglon.appendChild(lineas);
        esqueleto.appendChild(renglon);
      }
      cuerpo.appendChild(esqueleto);
      return;
    }

    if (error && !pagina) {
      cuerpo.appendChild(
        el('div', 'vault-campana__error', 'No pudimos cargar tus notificaciones. Cierra y vuelve a abrir la campana.')
      );
      return;
    }

    var acciones = (pagina && pagina.acciones) || [];
    var grupos = agruparActividad((pagina && pagina.avisos) || []);

    // La cascada: acciones primero, luego los grupos; dentro de un grupo que ya
    // entró, solo el aviso que llegó después. Lo que ya entró no se re-anima.
    var claves = [];
    acciones.forEach(function (a) {
      claves.push('a:' + a.clave);
    });
    grupos.forEach(function (g) {
      claves.push('g:' + g.clave);
      if (yaEntraron['g:' + g.clave]) {
        g.avisos.forEach(function (a) {
          claves.push('v:' + a.id);
        });
      }
    });
    var plan = retrasosDeEntrada(claves, yaEntraron);
    // Los avisos de un grupo que entra por primera vez entran con él.
    grupos.forEach(function (g) {
      g.avisos.forEach(function (a) {
        plan.vistos['v:' + a.id] = true;
      });
    });
    var retrasoDe = {};
    claves.forEach(function (c, n) {
      retrasoDe[c] = plan.retrasos[n];
    });
    yaEntraron = plan.vistos;

    // ── Te toca actuar ──
    cuerpo.appendChild(encabezado('Te toca actuar'));
    if (acciones.length === 0) {
      cuerpo.appendChild(
        vacio('CheckCheck', 'Nada pendiente. Te avisamos cuando algo dependa de ti.', 'vault-campana__icono--ok')
      );
    } else {
      var lista = el('ul', 'vault-campana__acciones');
      acciones.forEach(function (a) {
        var li = document.createElement('li');
        entrar(li, retrasoDe['a:' + a.clave]);
        li.appendChild(
          fila({
            titulo: a.titulo,
            cuerpo: a.cuerpo,
            nota: a.consecuencia,
            leida: !!a.leida,
            cartas: a.cartas,
            evento: a.tipo,
            asunto: a.asunto_tipo,
            // "Te toca actuar" sigue la misma regla que los avisos: recoger en
            // un punto enseña su mapa, no un ícono (F7).
            visual: visualEnFila(a, a.tipo, a.asunto_tipo),
            href: a.href,
            avisoIds: avisosDeLaAccion(a),
            meta: [
              chipDePlazo(a, ahora),
              // El pedido se nombra; la subasta ya se nombra en el cuerpo.
              a.asunto_tipo === 'orden' && a.asunto_etiqueta
                ? el('span', 'vault-campana__cuando', a.asunto_etiqueta)
                : null,
              cuando(a.desde, ahora),
            ],
          })
        );
        // "Ya lo recogí" (decisión del owner, 2026-10-02): el acuse va debajo
        // de la fila, no dentro — la fila ya es un enlace.
        if (a.tipo === 'recoger_pedido') li.appendChild(yaLoRecogi(a));
        lista.appendChild(li);
      });
      cuerpo.appendChild(lista);
    }

    // ── Actividad ──
    cuerpo.appendChild(encabezado('Actividad'));
    if (grupos.length === 0) {
      cuerpo.appendChild(vacio('Bell', 'Aquí verás tus pedidos, ventas, subastas y saldo.'));
    } else {
      var bloques = el('div', 'vault-campana__grupos');
      grupos.forEach(function (g, n) {
        var abierto = !!abiertos[g.clave];
        var bloque = el('div', 'vault-campana__grupo');
        entrar(bloque, retrasoDe['g:' + g.clave]);
        var disparador = el('button', 'vault-campana__grupo-cabecera');
        disparador.type = 'button';
        var idLista = 'vault-campana-grupo-' + n;
        disparador.setAttribute('aria-expanded', abierto ? 'true' : 'false');
        disparador.setAttribute('aria-controls', idLista);
        disparador.appendChild(el('span', 'vault-campana__grupo-nombre', g.etiqueta));
        disparador.appendChild(
          el('span', 'vault-campana__grupo-cuenta', g.avisos.length + ' ' + (g.avisos.length === 1 ? 'aviso' : 'avisos'))
        );
        disparador.appendChild(icono('ChevronDown', 'vault-campana__icono vault-campana__icono--chevron'));
        disparador.addEventListener('click', function () {
          if (abiertos[g.clave]) delete abiertos[g.clave];
          else abiertos[g.clave] = true;
          pintarBandeja();
        });
        bloque.appendChild(disparador);

        var ul = el('ul', 'vault-campana__avisos');
        ul.id = idLista;
        ul.hidden = !abierto;
        g.avisos.forEach(function (a) {
          var li = document.createElement('li');
          entrar(li, retrasoDe['v:' + a.id]);
          li.appendChild(
            fila({
              titulo: a.titulo,
              cuerpo: a.cuerpo,
              leida: !!a.read_at,
              cartas: a.cartas,
              evento: a.event_type,
              asunto: a.asunto_tipo,
              visual: visualDelAviso(a),
              href: a.href,
              avisoIds: a.id ? [a.id] : [],
              meta: [cuando(a.created_at, ahora)],
            })
          );
          ul.appendChild(li);
        });
        bloque.appendChild(ul);
        bloques.appendChild(bloque);
      });
      cuerpo.appendChild(bloques);
    }

    if (pagina && pagina.siguiente) {
      var mas = el('button', 'vault-campana__mas', cargandoMas ? 'Cargando…' : 'Ver avisos anteriores');
      mas.type = 'button';
      mas.disabled = cargandoMas;
      mas.addEventListener('click', cargarMas);
      cuerpo.appendChild(mas);
    }

    // "Estás al día" entra una vez por apertura, no en cada repintado.
    var alDia = estaAlDia(pagina);
    var entraAlDia = alDia && !yaEntraron['despedida:al-dia'];
    if (alDia) yaEntraron['despedida:al-dia'] = true;
    cuerpo.appendChild(despedida(alDia, entraAlDia));
  }

  // ── Verbos ──

  function abrirAviso(evento, destino, avisoIds) {
    if (avisoIds && avisoIds.length > 0) {
      void marcar({ ids: avisoIds });
      var ahora = new Date().toISOString();
      // Solo baja el número lo que de verdad estaba sin leer.
      var recienLeidos = {};
      if (pagina) {
        pagina.acciones.forEach(function (a) {
          // La entrega agrupada absorbió avisos de varios pedidos: bajan todos.
          var suyos = avisosDeLaAccion(a).filter(function (id) {
            return avisoIds.indexOf(id) !== -1;
          });
          if (suyos.length > 0 && !a.leida) {
            a.leida = true;
            suyos.forEach(function (id) {
              recienLeidos[id] = true;
            });
          }
          if (Array.isArray(a.avisos_por_leer)) {
            a.avisos_por_leer = a.avisos_por_leer.filter(function (id) {
              return avisoIds.indexOf(id) === -1;
            });
          }
        });
        pagina.avisos.forEach(function (a) {
          if (avisoIds.indexOf(a.id) !== -1 && !a.read_at) {
            a.read_at = ahora;
            recienLeidos[a.id] = true;
          }
        });
      }
      var bajan = Object.keys(recienLeidos).length;
      if (conteo && bajan > 0) {
        conteo.no_leidas = Math.max(0, conteo.no_leidas - bajan);
        guardarCache({ conteo: conteo });
        pintarBadge();
      }
    }
    if (!destino) {
      pintarBandeja();
      return;
    }
    // Ctrl/⌘/clic central: que el navegador abra la pestaña nueva como siempre.
    if (evento && (evento.metaKey || evento.ctrlKey || evento.shiftKey || evento.button === 1)) return;
    if (evento) evento.preventDefault();
    cerrar();
    window.location.assign(destino);
  }

  /**
   * "Ya lo recogí" en "Tu pedido te espera" — puerto de `YaLoRecogi` en
   * CardVault (`components/notificaciones/Bandeja.tsx`).
   *
   * Se ejecuta en el acto: es un acuse de un hecho, no una decisión. La ruta
   * toma el correo de la sesión (viaja sola, mismo sitio) y contesta con CORS
   * también en error. `text/plain` por la misma razón que `marcar`: petición
   * simple, sin preflight; la ruta lee el cuerpo con `request.json()`.
   */
  function yaLoRecogi(accion) {
    var caja = el('div', 'vault-campana__recogi');
    var b = el('button', 'vault-campana__recogi-boton');
    b.type = 'button';
    b.appendChild(icono('CheckCheck', 'vault-campana__icono vault-campana__icono--chico'));
    var etiqueta = el('span', null, 'Ya lo recogí');
    b.appendChild(etiqueta);
    caja.appendChild(b);
    var aviso = null;

    b.addEventListener('click', function () {
      b.disabled = true;
      etiqueta.textContent = 'Confirmando…';
      if (aviso) {
        caja.removeChild(aviso);
        aviso = null;
      }
      pedir('/api/orders/' + encodeURIComponent(accion.asunto_id) + '/confirm-delivery', {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
        body: '{}',
      })
        .then(function (r) {
          if (r.status === 401) {
            apagar();
            return null;
          }
          return r.json().then(
            function (dato) {
              return { ok: r.ok, dato: dato || {} };
            },
            function () {
              return { ok: r.ok, dato: {} };
            }
          );
        })
        .then(function (res) {
          if (!res) return;
          if (!res.ok) throw new Error(res.dato.error || '');
          resolverRecoleccion(accion);
        })
        .catch(function (e) {
          b.disabled = false;
          etiqueta.textContent = 'Ya lo recogí';
          aviso = el(
            'span',
            'vault-campana__recogi-error',
            (e && e.message) || 'No pudimos confirmarlo. Inténtalo otra vez.'
          );
          aviso.setAttribute('role', 'alert');
          caja.appendChild(aviso);
        });
    });
    return caja;
  }

  /** Confirmada: la acción sale de "Te toca actuar" y el badge baja uno. */
  function resolverRecoleccion(accion) {
    var ids = avisosDeLaAccion(accion);
    if (ids.length > 0) void marcar({ ids: ids });
    if (pagina) {
      pagina.acciones = pagina.acciones.filter(function (a) {
        return a.clave !== accion.clave;
      });
    }
    if (conteo) {
      conteo.acciones_pendientes = Math.max(0, conteo.acciones_pendientes - 1);
      if (!accion.leida && ids.length > 0) conteo.no_leidas = Math.max(0, conteo.no_leidas - ids.length);
      guardarCache({ conteo: conteo });
      pintarBadge();
    }
    pintarBandeja();
  }

  function marcarTodo() {
    var ahora = new Date().toISOString();
    if (pagina) {
      pagina.acciones.forEach(function (a) {
        a.leida = true;
      });
      pagina.avisos.forEach(function (a) {
        if (!a.read_at) a.read_at = ahora;
      });
    }
    if (conteo) {
      conteo.no_leidas = 0;
      guardarCache({ conteo: conteo });
    }
    todoAlDia = true;
    pintarBadge();
    pintarBandeja();
    marcar({ todas: true }).then(function () {
      ultimaPeticion = 0;
      refrescarConteo();
    });
  }

  // ── Abrir y cerrar ──

  function estaAbierta() {
    return !!panel && panel.classList.contains('is-abierta');
  }

  function abrir() {
    if (!panel || apagada) return;
    // El panel se muda al <body>: dentro del header sticky, el `transform` de
    // la animación del header lo encerraría en su caja.
    if (panel.parentNode !== document.body) document.body.appendChild(panel);
    panel.hidden = false;
    void panel.offsetWidth;
    panel.classList.add('is-abierta');
    document.body.classList.add('overflow-hidden');
    boton.setAttribute('aria-expanded', 'true');
    // Abrir NO marca leído: solo refresca lo que se ve.
    todoAlDia = false;
    // Cada apertura estrena su cascada.
    yaEntraron = {};
    cargarBandeja();
    ultimaPeticion = 0;
    refrescarConteo();
    var cerrarBtn = panel.querySelector('[data-campana-cerrar]');
    if (cerrarBtn) cerrarBtn.focus();
  }

  function cerrar() {
    if (!estaAbierta()) return;
    panel.classList.remove('is-abierta');
    document.body.classList.remove('overflow-hidden');
    boton.setAttribute('aria-expanded', 'false');
    window.setTimeout(function () {
      if (!estaAbierta()) panel.hidden = true;
    }, 250);
    boton.focus();
  }

  // ── Arranque ──

  function iniciar() {
    boton = document.getElementById('vault-campana');
    panel = document.getElementById('vault-campana-bandeja');
    if (!boton || !panel) return;
    // Sin la cookie visual no hay nadie a quien avisar: ni se pinta ni se pide.
    if (!hayUsuarioVisual(document.cookie)) {
      boton.style.display = 'none';
      return;
    }

    burbuja = boton.querySelector('[data-campana-burbuja]');
    cuerpo = panel.querySelector('[data-campana-cuerpo]');
    accionCabecera = panel.querySelector('[data-campana-accion]');

    var guardado = leerCache();
    if (guardado && guardado.sinSesion) {
      // La app ya dijo "no hay sesión" hace menos de un minuto.
      apagada = true;
      boton.style.display = 'none';
      return;
    }
    // La visibilidad antes del primer conteo la decidió el script en línea del
    // header (sin parpadeo); con el conteo la decide `pintarBadge`.

    if (guardado && guardado.conteo) {
      conteo = guardado.conteo;
      pintarBadge();
    } else {
      refrescarConteo();
    }

    boton.addEventListener('click', function () {
      if (estaAbierta()) cerrar();
      else abrir();
    });

    panel.addEventListener('click', function (e) {
      // Clic afuera de la hoja (el velo) o en la X. Se pregunta por el velo y no
      // por "fuera de la hoja": un botón que se repinta al hacer clic (Ver
      // avisos anteriores, plegar un bloque) ya no está en la hoja cuando el
      // evento sube, y eso cerraba la bandeja.
      var t = e.target;
      if (t === panel || (t && t.closest && (t.closest('[data-campana-velo]') || t.closest('[data-campana-cerrar]')))) {
        cerrar();
      }
    });

    document.addEventListener('keydown', function (e) {
      if (!estaAbierta()) return;
      if (e.key === 'Escape' || e.key === 'Esc') {
        e.preventDefault();
        cerrar();
      } else if (e.key === 'Tab') {
        atraparFoco(e);
      }
    });

    var alVolver = function () {
      if (document.visibilityState === 'visible') refrescarConteo();
    };
    window.addEventListener('focus', alVolver);
    document.addEventListener('visibilitychange', alVolver);
    reloj = window.setInterval(function () {
      if (document.visibilityState === 'visible') {
        ultimaPeticion = 0;
        refrescarConteo();
      }
    }, INTERVALO_MS);
  }

  /** El foco no se escapa del panel mientras está abierto. */
  function atraparFoco(e) {
    var hoja = panel.querySelector('[data-campana-hoja]');
    if (!hoja) return;
    var enfocables = hoja.querySelectorAll('a[href], button:not([disabled])');
    if (enfocables.length === 0) return;
    var primero = enfocables[0];
    var ultimo = enfocables[enfocables.length - 1];
    if (e.shiftKey && document.activeElement === primero) {
      e.preventDefault();
      ultimo.focus();
    } else if (!e.shiftKey && document.activeElement === ultimo) {
      e.preventDefault();
      primero.focus();
    } else if (!hoja.contains(document.activeElement)) {
      e.preventDefault();
      primero.focus();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', iniciar);
  } else {
    iniciar();
  }
})();
